import { useState } from "react";
import { motion } from "framer-motion";
import { X, MoonStar, CheckCircle2, Loader2 } from "lucide-react";
import { VoiceButton } from "./VoiceButton";

const QUESTIONS = [
  { key: "done", label: "What did you get done today?", placeholder: "Wins, jobs moved along, calls made…" },
  { key: "open", label: "What's still open or didn't get to?", placeholder: "Anything left hanging…" },
  { key: "tomorrow", label: "What needs doing tomorrow?", placeholder: "One thought per line — each becomes a task", tasks: true },
  { key: "blockers", label: "Anything bugging you or in the way?", placeholder: "Get it off your chest…" },
];

// Interactive end-of-day check-in. Shows the day's recap, asks a few questions
// (voice or type), and files tomorrow's list as tasks.
export const EndOfDayModal = ({ recap, onClose, onSave }) => {
  const [answers, setAnswers] = useState({ done: "", open: "", tomorrow: "", blockers: "" });
  const [saving, setSaving] = useState(false);

  const stats = [
    { label: "Job steps done", value: recap?.stages_completed_today ?? 0, accent: "#22c55e" },
    { label: "Hours logged", value: `${recap?.hours_logged_today ?? 0}h`, accent: "#3b82f6" },
    { label: "New quotes", value: recap?.quotes_today ?? 0, accent: "#f59e0b" },
    { label: "Open tasks", value: recap?.open_tasks ?? 0, accent: "#8b5cf6" },
  ];

  const handleSave = async () => {
    setSaving(true);
    const tomorrow_tasks = (answers.tomorrow || "")
      .split("\n").map((l) => l.trim()).filter(Boolean);
    await onSave({ answers, tomorrow_tasks });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="end-of-day-modal">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto border"
        style={{ backgroundColor: "var(--dash-card)", borderColor: "var(--dash-border)" }}
      >
        <div className="flex items-center justify-between p-5 border-b sticky top-0" style={{ borderColor: "var(--dash-border)", backgroundColor: "var(--dash-card)" }}>
          <div className="flex items-center gap-2">
            <MoonStar size={20} className="text-[#8b5cf6]" />
            <h2 className="font-heading text-lg uppercase tracking-wide" style={{ color: "var(--dash-text)" }}>End of day</h2>
          </div>
          <button onClick={onClose} data-testid="end-of-day-close" className="text-zinc-500 hover:text-white bg-transparent border-none cursor-pointer">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Recap */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {stats.map((s) => (
              <div key={s.label} className="border p-3 text-center" style={{ borderColor: "var(--dash-border)" }} data-testid={`recap-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="font-heading text-2xl" style={{ color: s.accent }}>{s.value}</div>
                <div className="font-body text-[10px] uppercase tracking-wider mt-1" style={{ color: "var(--dash-text-dim)" }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Questions */}
          {QUESTIONS.map((q) => (
            <div key={q.key}>
              <label className="font-body text-sm block mb-2" style={{ color: "var(--dash-text)" }}>{q.label}</label>
              <div className="flex gap-2 items-start">
                <textarea
                  value={answers[q.key]}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
                  placeholder={q.placeholder}
                  data-testid={`checkin-${q.key}`}
                  className="flex-1 h-20 bg-transparent border p-2 outline-none font-body text-sm resize-none focus:border-[#8b5cf6]/50 transition-colors"
                  style={{ color: "var(--dash-text)", borderColor: "var(--dash-border)" }}
                />
                <VoiceButton
                  onText={(t) => setAnswers((a) => ({ ...a, [q.key]: (a[q.key] ? a[q.key] + (q.tasks ? "\n" : " ") : "") + t.trim() }))}
                  size={18}
                  testId={`checkin-voice-${q.key}`}
                  title="Speak your answer"
                  className="w-10 h-10 border border-[#8b5cf6]/40 text-[#8b5cf6] rounded-full flex-shrink-0"
                />
              </div>
            </div>
          ))}

          <button onClick={handleSave} disabled={saving} data-testid="end-of-day-save"
            className="w-full flex items-center justify-center gap-2 min-h-[52px] font-heading text-sm uppercase tracking-[0.12em] bg-[#8b5cf6] text-white border-none cursor-pointer disabled:opacity-50">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <><CheckCircle2 size={18} /> Save & wrap up</>}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default EndOfDayModal;
